import { FC, useRef } from "react"
import {
  ConnectorFormValues,
  ConnectorDirection
} from "../../../../../../../types"
import { FormikErrors } from "formik"
import ConnectorFormInput from "../../components/ConnectorFormInput"
import TemplateEditor from "../../template-editor"

interface ConfigurationSectionProps {
  values: ConnectorFormValues
  setFormField: (name: string, val: any) => void
  isCreate: boolean
  errors: FormikErrors<ConnectorFormValues>
  onContentTypeChange: (contentType: string) => void
  onDataTypeChange: (dataType: string) => void
  availableTemplates: Array<{ name: string; field_mapping: any; type: string }>
  onTemplateLoad: (templateName: string) => void
}

const ConfigurationSection: FC<ConfigurationSectionProps> = ({
  values,
  setFormField,
  isCreate,
  errors,
  onContentTypeChange,
  onDataTypeChange,
  availableTemplates,
  onTemplateLoad
}) => {
  const previousDirectionRef = useRef<ConnectorDirection>(values.direction)

  const handleDirectionChange = (newDirection: ConnectorDirection) => {
    const hasFieldMapping =
      values.field_mapping && values.field_mapping.length > 0

    if (hasFieldMapping && previousDirectionRef.current !== newDirection) {
      const confirmed = window.confirm(
        "Changing the direction will clear all field mapping settings. Are you sure you want to continue?"
      )

      if (confirmed) {
        setFormField("direction", newDirection)
        setFormField("field_mapping", [])
        previousDirectionRef.current = newDirection
      }
    } else {
      setFormField("direction", newDirection)
      previousDirectionRef.current = newDirection
    }
  }

  return (
    <div className="border-b pb-6">
      <h3 className="text-lg font-medium mb-4">Configuration</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Direction *
          </label>
          {isCreate ? (
            <>
              <select
                value={values.direction}
                onChange={e =>
                  handleDirectionChange(e.target.value as ConnectorDirection)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="outgoing">→ Outgoing (BB → CRM)</option>
                <option value="incoming">← Incoming (CRM → BB)</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {values.direction === "outgoing" &&
                  "Sends data to your CRM when records are created in Banking Bridge"}
                {values.direction === "incoming" &&
                  "Receives data from your CRM to create records in Banking Bridge"}
              </p>
            </>
          ) : (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
              {values.direction === "outgoing" && "→ Outgoing (BB → CRM)"}
              {values.direction === "incoming" && "← Incoming (CRM → BB)"}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data Type *
          </label>
          {isCreate ? (
            <select
              value={values.data_type}
              onChange={e => onDataTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="lead">Lead</option>
              <option value="subscription">Subscription</option>
              <option value="pending_subscription">Pending Subscription</option>
            </select>
          ) : (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
              {values.data_type === "lead" && "Lead"}
              {values.data_type === "subscription" && "Subscription"}
              {values.data_type === "pending_subscription" &&
                "Pending Subscription"}
            </div>
          )}
        </div>

        {values.direction === "outgoing" && (
          <>
            <ConnectorFormInput
              label="CRM Webhook URL"
              value={values.webhook_url || ""}
              onChange={value => setFormField("webhook_url", value)}
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              error={errors.webhook_url}
              helpText="The URL where Banking Bridge will send data"
              type="url"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content Type *
              </label>
              <select
                value={values.config?.content_type || "application/json"}
                onChange={e => onContentTypeChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="application/json">
                  JSON (application/json)
                </option>
                <option value="application/x-www-form-urlencoded">
                  URL-Encoded (application/x-www-form-urlencoded)
                </option>
                <option value="multipart/form-data">
                  Form Data (multipart/form-data)
                </option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                How data will be formatted when sent to your CRM (automatically
                sets Content-Type header)
              </p>
            </div>

            <div className="border-t pt-4">
              <TemplateEditor
                template={values.template || ""}
                onChange={value => setFormField("template", value)}
                dataType={values.data_type}
                contentType={values.config?.content_type}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ConfigurationSection
